import { useRef, useState } from 'react'
import { ReactFlowProvider } from '@xyflow/react'
import { Button } from '@/components/ui/button'
import { useOrgChart, useImportOrgChartCsv } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { Network, Upload, GitBranch, List } from 'lucide-react'
import { OrgChartDiagram } from './orgchart/OrgChartDiagram'
import { OrgChartListView } from './orgchart/OrgChartListView'
import { explainError } from '@/lib/explain-error'

type View = 'diagram' | 'list'

export default function OrgChartPage() {
  const { data, isLoading, error } = useOrgChart()
  const [view, setView] = useState<View>('diagram')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const importCsv = useImportOrgChartCsv()
  const { toast } = useToast()

  const roots = data?.data ?? []
  const total = data?.count ?? 0

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const csv = await file.text()
      const summary = await importCsv.mutateAsync(csv)
      toast({
        title: 'Org chart imported',
        description: `${summary.created} added, ${summary.updated} updated, ${summary.retired} retired, ${summary.departmentsCreated} new departments.`,
      })
      if (summary.warnings.length > 0) {
        toast({
          title: `${summary.warnings.length} warning${summary.warnings.length === 1 ? '' : 's'}`,
          description: `${summary.warnings.slice(0, 8).join(' • ')}. They have been queued for retry.`,
        })
      }
    } catch (err: any) {
      toast({
        title: 'Import failed',
        description: explainError(err, 'Could not import the CSV.'),
        variant: 'destructive',
      })
    }
  }

  return (
    <main className="w-full px-4 py-4">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Network className="h-6 w-6" aria-hidden="true" />
            Org Chart
          </h1>
          <p className="text-sm text-muted-foreground">
            {total} active {total === 1 ? 'employee' : 'employees'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div
            className="inline-flex overflow-hidden rounded-md border border-input"
            role="group"
            aria-label="Org chart view"
          >
            <button
              type="button"
              onClick={() => setView('diagram')}
              aria-pressed={view === 'diagram'}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
                view === 'diagram' ? 'bg-primary text-primary-foreground' : 'bg-background'
              }`}
            >
              <GitBranch className="h-4 w-4" aria-hidden="true" />
              Diagram
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              className={`flex items-center gap-1 border-l border-input px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
                view === 'list' ? 'bg-primary text-primary-foreground' : 'bg-background'
              }`}
            >
              <List className="h-4 w-4" aria-hidden="true" />
              List
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            aria-hidden="true"
            tabIndex={-1}
            onChange={handleFileChange}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={importCsv.isPending}
            aria-label="Import org chart from a CSV file"
          >
            <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
            {importCsv.isPending ? 'Importing…' : 'Import CSV'}
          </Button>
        </div>
      </header>

      {isLoading && (
        <p role="status" aria-live="polite" className="text-muted-foreground">
          Loading org chart…
        </p>
      )}
      {error && (
        <p role="alert" className="text-destructive">
          Failed to load org chart.
        </p>
      )}

      {!isLoading && !error && (
        <>
          {view === 'list' ? (
            <OrgChartListView roots={roots} />
          ) : roots.length === 0 ? (
            <p className="text-muted-foreground">
              No employees yet. Add a person or import a CSV to start building your org chart.
            </p>
          ) : (
            <ReactFlowProvider>
              <OrgChartDiagram roots={roots} dottedLines={data?.dottedLines ?? []} />
            </ReactFlowProvider>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Tip: drag a card onto another person to change who they report to. Double-click a card
            to focus on their team. Right-click for more actions. Prefer a keyboard/screen-reader
            view? Switch to <span className="font-medium">List</span>.
          </p>
        </>
      )}
    </main>
  )
}
